// File uploader for templates/file/_uploader.html.twig (product photos, arrival files) and the public phone page.
(() => {
    const GENERIC_ERROR = 'Не удалось выполнить запрос. Проверьте соединение и повторите попытку.';
    const POLL_INTERVAL = 4000;
    const EXTENSIONS = {
        'image/jpeg': ['jpg', 'jpeg'],
        'image/png': ['png'],
        'image/webp': ['webp'],
        'application/pdf': ['pdf'],
        'application/vnd.ms-excel': ['xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
    };

    function decode(text, status) {
        let data = null;
        try { data = JSON.parse(text); } catch (error) { /* handled below */ }
        if (status < 200 || status >= 300 || !data || typeof data !== 'object' || data.error) {
            // A 413 without JSON comes from nginx before the request reaches the application.
            const fallback = status === 413 ? 'Файл слишком большой для сервера. Выберите файл меньшего размера.' : GENERIC_ERROR;
            const failure = new Error(data?.error?.message || fallback);
            failure.status = status;
            throw failure;
        }
        return data;
    }

    function formData(fields) {
        const data = new FormData();
        Object.entries(fields).forEach(([name, value]) => { if (value !== undefined) data.append(name, value); });
        return data;
    }

    async function request(url, fields) {
        const response = await fetch(url, {
            method: fields ? 'POST' : 'GET',
            body: fields ? formData(fields) : undefined,
            headers: {Accept: 'application/json'},
            credentials: 'same-origin',
        });
        return decode(await response.text(), response.status);
    }

    function node(tag, className, text) {
        const element = document.createElement(tag);
        element.className = className;
        if (text !== undefined) element.textContent = text;
        return element;
    }

    function setText(element, text) {
        // Live regions re-announce on every write, so only touch them on real changes.
        if (element && element.textContent !== text) element.textContent = text;
    }

    function hasFiles(event) {
        return Array.from(event.dataTransfer?.types || []).includes('Files');
    }

    function initUploader(root) {
        const config = JSON.parse(root.dataset.fileUploader);
        const publicPage = !!config.public;
        const maxSizeLabel = `${Math.round(config.maxSize / 1048576)} МБ`;
        const acceptsImages = config.contentTypes.includes('image/jpeg');
        const extensions = [...config.contentTypes.flatMap(type => EXTENSIONS[type] || []), ...(acceptsImages ? ['heic', 'heif'] : [])];
        const list = root.querySelector('[data-file-list]');
        const emptyNote = root.querySelector('[data-file-empty]');
        const template = root.querySelector('[data-file-template]');
        const queueNode = root.querySelector('[data-file-queue]');
        const message = root.querySelector('[data-file-message]');
        const remainingNode = root.querySelector('[data-file-remaining]');
        const dropzone = root.querySelector('[data-file-dropzone]');
        const inputs = root.querySelectorAll('[data-file-input]');
        const queue = [];
        let remaining = config.remaining;
        let expired = false;
        let uploading = false;
        let mobileSession = null;

        const showError = text => setText(message, text || '');
        const atLimit = () => remaining !== null && remaining <= 0;
        const accepting = () => !expired && !atLimit();

        function update() {
            inputs.forEach(input => { input.disabled = !accepting(); });
            dropzone?.classList.toggle('is-disabled', !accepting());
            setText(remainingNode, expired
                ? 'Срок действия ссылки истёк. Загруженные файлы сохранены.'
                : remaining === null ? ''
                    : `Можно добавить ещё ${Math.max(0, remaining)} из ${config.maxCount}`);
        }

        // Files already attached

        function fillItem(item, file) {
            item.dataset.fileId = file.id;
            item.querySelectorAll('[data-file-link]').forEach(link => {
                link.href = file.url;
                link.title = file.name;
                link.toggleAttribute('data-file-lightbox', file.is_image);
            });
            item.querySelectorAll('[data-file-image]').forEach(image => { image.src = file.url; image.alt = file.name; });
            item.querySelectorAll('[data-file-name]').forEach(name => { name.textContent = file.name; name.title = file.name; });
            item.querySelectorAll('[data-file-extension]').forEach(badge => { badge.textContent = file.extension; });
            item.querySelectorAll('[data-file-size]').forEach(size => { size.textContent = file.size; });
            const remove = item.querySelector('[data-file-remove]');
            if (file.remove_url) {
                remove.dataset.url = file.remove_url;
            } else {
                remove?.remove();
            }
            return item;
        }

        function createItem(file) {
            return fillItem(template.content.firstElementChild.cloneNode(true), file);
        }

        function syncEmpty() {
            if (emptyNote) emptyNote.hidden = list.children.length > 0;
        }

        // Reuses existing items so unchanged images are not downloaded again.
        function renderFiles(files) {
            const existing = new Map(Array.from(list.children, item => [item.dataset.fileId, item]));
            const items = files.map(file => existing.get(String(file.id)) || createItem(file));
            const changed = items.length !== list.children.length || items.some((item, index) => list.children[index] !== item);
            if (changed) list.replaceChildren(...items);
            syncEmpty();
        }

        async function refresh() {
            if (!config.listUrl) return;
            try {
                const data = await request(config.listUrl);
                remaining = data.remaining;
                renderFiles(data.files);
                update();
                showError('');
            } catch (error) { showError(error.message); }
        }

        list?.addEventListener('click', async event => {
            const button = event.target.closest('[data-file-remove]');
            if (!button || button.disabled) return;
            const item = button.closest('[data-file-item]');
            button.disabled = true;
            item.classList.add('is-removing');
            try {
                const data = await request(button.dataset.url, {_token: config.token});
                item.remove();
                remaining = data.remaining;
                showError('');
                syncEmpty();
                update();
                pump();
            } catch (error) {
                showError(error.message);
                item.classList.remove('is-removing');
                button.disabled = false;
            }
        });

        // Upload queue

        function renderQueued(entry) {
            const labels = {
                waiting: 'В очереди',
                uploading: entry.percent === 100 ? 'Обработка…' : `Загрузка: ${entry.percent}%`,
                done: 'Загружено',
                error: 'Не загружено',
            };
            entry.row.className = `file-uploader__queue-item file-uploader__queue-item--${entry.state}`;
            entry.status.textContent = labels[entry.state];
            entry.reason.textContent = entry.detail;
            entry.reason.hidden = !entry.detail;
            entry.progress.hidden = entry.state !== 'uploading';
            entry.progress.value = entry.percent;
            entry.retry.hidden = entry.state !== 'error' || !entry.retryable || expired;
            entry.dismiss.hidden = entry.state !== 'error' && entry.state !== 'waiting';
            const dismissLabel = `${entry.state === 'waiting' ? 'Отменить загрузку' : 'Убрать из списка'}: ${entry.name}`;
            entry.dismiss.title = dismissLabel;
        }

        function setState(entry, state, detail = '', retryable = false) {
            Object.assign(entry, {state, detail, retryable});
            renderQueued(entry);
        }

        function validationError(file) {
            if (file.size > config.maxSize) return `Файл больше ${maxSizeLabel}.`;
            const extension = file.name.match(/\.([a-z0-9]{1,8})$/i)?.[1]?.toLowerCase();
            const supported = extension
                ? extensions.includes(extension)
                : config.contentTypes.includes(file.type) || (acceptsImages && /^image\/hei[cf]/i.test(file.type));
            if (supported) return null;
            return extension ? `Формат ${extension.toUpperCase()} не поддерживается.` : 'Этот формат не поддерживается.';
        }

        function createQueued(file) {
            const entry = {file, name: file.name, id: crypto.randomUUID(), state: 'waiting', detail: '', retryable: false, percent: 0};
            entry.row = node('div', 'file-uploader__queue-item');
            const content = node('div', '');
            const header = node('div', 'file-uploader__queue-header');
            const title = node('div', 'file-uploader__queue-name', file.name);
            entry.status = node('div', 'file-uploader__queue-status');
            entry.reason = node('div', 'file-uploader__queue-reason');
            entry.progress = node('progress', 'file-uploader__progress');
            entry.progress.max = 100;
            entry.retry = node('button', 'file-uploader__queue-retry', 'Повторить');
            entry.retry.type = 'button';
            entry.dismiss = node('button', 'file-uploader__queue-dismiss', '×');
            entry.dismiss.type = 'button';
            entry.retry.addEventListener('click', () => {
                if (entry.state !== 'error' || expired) return;
                entry.percent = 0;
                setState(entry, 'waiting');
                pump();
            });
            entry.dismiss.addEventListener('click', () => {
                if (entry.state !== 'waiting' && entry.state !== 'error') return;
                queue.splice(queue.indexOf(entry), 1);
                entry.row.remove();
            });
            header.append(title, entry.status);
            content.append(header, entry.reason, entry.progress, entry.retry);
            entry.row.append(content, entry.dismiss);
            return entry;
        }

        function send(entry) {
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open('POST', config.uploadUrl);
                xhr.setRequestHeader('Accept', 'application/json');
                xhr.timeout = 180000;
                xhr.upload.onprogress = event => {
                    if (!event.lengthComputable) return;
                    entry.percent = Math.round(event.loaded / event.total * 100);
                    renderQueued(entry);
                };
                xhr.onload = () => {
                    try { resolve(decode(xhr.responseText, xhr.status)); } catch (error) { reject(error); }
                };
                xhr.onerror = () => reject(new Error(GENERIC_ERROR));
                xhr.ontimeout = () => reject(new Error('Загрузка заняла слишком много времени. Повторите попытку.'));
                xhr.send(formData({_token: config.token, file: entry.file, upload_id: entry.id}));
            });
        }

        async function pump() {
            if (uploading || expired) return;
            const entry = queue.find(candidate => candidate.state === 'waiting');
            if (!entry) return;
            if (atLimit()) {
                queue.filter(candidate => candidate.state === 'waiting').forEach(candidate => {
                    setState(candidate, 'error', 'Достигнут лимит файлов. Удалите лишние и повторите.', true);
                });
                return;
            }
            uploading = true;
            entry.percent = 0;
            setState(entry, 'uploading');
            try {
                const data = await send(entry);
                remaining = data.remaining;
                if (data.file && list && !list.querySelector(`[data-file-id="${data.file.id}"]`)) {
                    list.append(createItem(data.file));
                    syncEmpty();
                }
                entry.file = null;
                setState(entry, 'done');
            } catch (error) {
                // Retries reuse the upload id, so a lost response never creates a duplicate.
                const retryable = !error.status || error.status >= 500 || error.status === 409;
                setState(entry, 'error', error.message, retryable);
            } finally {
                uploading = false;
                update();
                pump();
            }
        }

        function addFiles(files) {
            if (!accepting()) {
                if (!expired) showError('Достигнут лимит файлов. Удалите лишние, чтобы добавить новые.');
                return;
            }
            showError('');
            Array.from(files).forEach(file => {
                const entry = createQueued(file);
                const error = validationError(file);
                if (error) {
                    entry.file = null;
                    entry.state = 'error';
                    entry.detail = error;
                }
                renderQueued(entry);
                queueNode.append(entry.row);
                queue.push(entry);
            });
            pump();
        }

        inputs.forEach(input => input.addEventListener('change', () => { addFiles(input.files); input.value = ''; }));
        if (dropzone) {
            ['dragenter', 'dragover'].forEach(type => dropzone.addEventListener(type, event => {
                if (!hasFiles(event)) return;
                event.preventDefault();
                dropzone.classList.toggle('is-dragging', accepting());
            }));
            dropzone.addEventListener('dragleave', event => {
                if (!dropzone.contains(event.relatedTarget)) dropzone.classList.remove('is-dragging');
            });
            dropzone.addEventListener('drop', event => {
                event.preventDefault();
                dropzone.classList.remove('is-dragging');
                addFiles(event.dataTransfer.files);
            });
        }

        // Phone upload link (QR modal)

        const modal = root.querySelector('[data-file-session-modal]');
        if (modal) {
            const qr = modal.querySelector('[data-file-session-qr]');
            const timer = modal.querySelector('[data-file-session-timer]');
            const status = modal.querySelector('[data-file-session-status]');
            const copyButton = modal.querySelector('[data-file-session-copy]');
            const createButton = modal.querySelector('[data-file-session-create]');
            const revokeButton = modal.querySelector('[data-file-session-revoke]');
            const limitText = 'Достигнут лимит файлов: с телефона добавить не получится.';
            let busy = false;

            const secondsLeft = () => mobileSession ? Math.max(0, Math.ceil((Date.parse(mobileSession.expires_at) - Date.now()) / 1000)) : 0;

            function renderSession() {
                const seconds = secondsLeft();
                const creating = busy && !seconds;
                qr.hidden = !seconds && !creating;
                copyButton.hidden = !seconds;
                revokeButton.hidden = !seconds;
                createButton.hidden = busy || !!seconds;
                if (creating) timer.textContent = 'Создаём код…';
                else if (seconds) timer.textContent = `Код действует ещё ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
                else timer.textContent = mobileSession ? 'Код истёк.' : '';
                if (seconds && atLimit()) setText(status, limitText);
                else if (status.textContent === limitText) setText(status, '');
            }

            async function run(action) {
                if (busy) return;
                busy = true;
                [createButton, revokeButton].forEach(button => { button.disabled = true; });
                renderSession();
                try { await action(); } catch (error) {
                    if (!mobileSession) qr.replaceChildren();
                    setText(status, error.message);
                } finally {
                    busy = false;
                    [createButton, revokeButton].forEach(button => { button.disabled = false; });
                    renderSession();
                }
            }

            const createSession = () => run(async () => {
                setText(status, '');
                mobileSession = null;
                qr.replaceChildren(node('div', 'spinner-border text-secondary'));
                qr.firstChild.setAttribute('role', 'presentation');
                renderSession();
                mobileSession = await request(config.sessionUrl, {_token: config.token});
                // SVG is generated by our authenticated endpoint, never supplied by a file.
                qr.innerHTML = mobileSession.qr_svg;
            });

            const revokeSession = () => run(async () => {
                await request(config.revokeUrl, {_token: config.token});
                mobileSession = null;
                qr.replaceChildren();
                setText(status, 'Ссылка отключена. Загруженные файлы сохранены.');
                await refresh();
            });

            // The countdown only matters while the dialog is visible.
            let ticker = null;
            modal.addEventListener('show.bs.modal', () => {
                if (!secondsLeft()) createSession();
                renderSession();
                ticker = setInterval(renderSession, 1000);
            });
            modal.addEventListener('hidden.bs.modal', () => {
                clearInterval(ticker);
                ticker = null;
                refresh();
            });
            createButton.addEventListener('click', createSession);
            revokeButton.addEventListener('click', revokeSession);
            copyButton.addEventListener('click', async () => {
                try {
                    await navigator.clipboard.writeText(mobileSession.url);
                    setText(status, 'Ссылка скопирована.');
                } catch (error) {
                    setText(status, 'Не удалось скопировать ссылку.');
                }
            });
            // Files from the phone appear here only while its link is alive.
            setInterval(() => { if (secondsLeft() && !document.hidden && !uploading) refresh(); }, POLL_INTERVAL);
            document.addEventListener('visibilitychange', () => { if (secondsLeft() && !document.hidden) refresh(); });
        }

        if (publicPage) {
            setInterval(() => {
                if (expired || Date.parse(config.expiresAt) > Date.now()) return;
                expired = true;
                queue.filter(entry => entry.state === 'waiting').forEach(entry => {
                    setState(entry, 'error', 'Срок действия ссылки истёк. Файл не загружен.');
                });
                queue.forEach(renderQueued);
                update();
            }, 5000);
        }
        update();
    }

    addEventListener('DOMContentLoaded', () => {
        document.addEventListener('click', event => {
            const link = event.target.closest('[data-file-lightbox]');
            if (!link || !window.Lightbox) return;
            event.preventDefault();
            new window.Lightbox(link).show();
        });
        // A file dropped outside the drop zone would otherwise navigate away from an unsaved form.
        if (document.querySelector('[data-file-dropzone]')) {
            ['dragover', 'drop'].forEach(type => document.addEventListener(type, event => {
                if (hasFiles(event) && !event.target.closest?.('[data-file-dropzone], input[type=file]')) {
                    event.preventDefault();
                    if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
                }
            }));
        }
        document.querySelectorAll('[data-file-uploader]').forEach(initUploader);
    });
})();
